import { useEffect } from 'react';
import { useGcsStore } from '../../store/useGcsStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import { useFleetVisibilityStore } from '../../store/useFleetVisibilityStore';
import { useSelectedVehicleStore } from '../../store/useSelectedVehicleStore';
import { selectIsVehicleUnderMyControl } from '../../store/selectors/selectIsVehicleUnderMyControl';
import { DEFAULT_NOTIFICATION_DURATION_MS, InAppNotificationType } from '../../types/ui/inAppNotification';
import { en } from '../../locales/en';
import type { GcsStore } from '../../store/useGcsStore';
import type { VehicleId } from '../../types/gcs/vehicle';
import { getShortVehicleId } from '../../utils/formatters';

interface GcsVehicleMcErrorListenerProps {
  gcsId: string;
}

export function GcsVehicleMcErrorListener({ gcsId }: GcsVehicleMcErrorListenerProps) {
  useEffect(() => {
    const selectMcErrorDescriptions = (state: GcsStore): Map<VehicleId, string | null | undefined> => {
      const errors = new Map<VehicleId, string | null | undefined>();
      const gcsData = state.data.get(gcsId);

      if (gcsData) {
        gcsData.vehicleMissionComputerStatusMap.forEach((status, vehicleId) => {
          errors.set(vehicleId, status.errorDescription);
        });
      }

      return errors;
    };

    const equalityFn = (
      a: Map<VehicleId, string | null | undefined>,
      b: Map<VehicleId, string | null | undefined>
    ): boolean => {
      if (a.size !== b.size) return false;
      for (const [key, value] of a) {
        if (b.get(key) !== value) return false;
      }
      return true;
    };

    const unsubscribe = useGcsStore.subscribe(
      selectMcErrorDescriptions,
      (nextErrors, prevErrors = new Map()) => {
        const showNotification = useNotificationStore.getState().showNotification;
        const selectVehicle = useSelectedVehicleStore.getState().selectVehicle;

        nextErrors.forEach((nextError, vehicleId) => {
          const previousError = prevErrors.get(vehicleId);

          // Skip if this is the first time seeing this vehicle
          if (previousError === undefined) return;

          // Only notify when error is non-null and has changed
          if (nextError != null && nextError !== previousError) {
            const fleetVisibility = useFleetVisibilityStore.getState().visibility;
            if (
              fleetVisibility === 'mine' &&
              !selectIsVehicleUnderMyControl(useGcsStore.getState().data, gcsId, vehicleId)
            ) {
              return;
            }

            showNotification({
              type: InAppNotificationType.ERROR,
              title: `${getShortVehicleId(vehicleId)}: ${en.vehicleMcError.title}`,
              body: nextError,
              durationMs: DEFAULT_NOTIFICATION_DURATION_MS,
              onClick: () => selectVehicle(vehicleId, gcsId)
            });
          }
        });
      },
      {
        fireImmediately: true,
        equalityFn
      }
    );

    return () => {
      unsubscribe();
    };
  }, [gcsId]);

  return null;
}
